<template>
  <div class="message-module-wrapper">
    <!-- POPUP MODAL OVERLAY -->
    <transition name="modal-fade">
      <div class="modal-backdrop" role="dialog" aria-modal="true">
        <div class="modal-panel">
          <!-- MODAL HEADER -->
          <header class="modal-header">
            <div class="header-actions">
              <button class="icon-action-btn close-btn" title="Close (Esc)" @click="closePopup">
                ✕
              </button>
            </div>
          </header>

          <!-- COLLAPSIBLE CONTENT AREA -->
          <div class="modal-scroll-area">
            <!-- SENDER & SUBJECT METADATA -->
            <section class="message-meta-section">
              <div class="sender-profile">
                <div class="profile-details">
                  <div class="name-line">
                    <h3 v-if="message.sender">
                      {{ message.sender.first_name }} {{ message.sender.last_name }}
                    </h3>
                  </div>
                  <div class="recipient-line">
                    <time class="meta-time">{{ message.timestamp | LocalDateFormat }} at {{ message.timestamp | LocalTimeFormat }}</time>
                  </div>
                </div>
              </div>
            </section>

            <!-- FULL MESSAGE BODY -->
            <section class="message-body-section">
              <div class="message-content" v-html="formattedContent"></div>

              <!-- Attachment Chip with dynamic Name & Size -->
              <div v-if="message.file" class="attachments-wrap">
                <a
                  :href="message.file"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="attachment-chip"
                  title="Click to view/download"
                >
                  <span class="file-icon">📄</span>
                  <div class="file-info">
                    <span class="file-name">{{ attachmentFileName }}</span>
                    <!-- <span class="file-size">{{ attachmentFileSize }}</span> -->
                  </div>
                </a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { bus } from '@/main';

export default {
  name: 'MessageReplyModal',
  props: {
    message: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      fileBytes: null,
      isLoadingFileSize: false
    };
  },
  computed: {
    formattedContent() {
      if (!this.message || !this.message.message) return '';
      return this.message.message
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>');
    },

    // Extracts file name cleanly from URL path without query parameters
    attachmentFileName() {
      if (!this.message || !this.message.file) return '';
      try {
        const parsed = new URL(this.message.file);
        const segments = parsed.pathname.split('/').filter(Boolean);
        return segments.length ? segments[segments.length - 1] : 'attachment';
      } catch (e) {
        const cleanUrl = this.message.file.split('?')[0];
        return cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1) || 'attachment';
      }
    },

    // Formats raw byte count into human-readable notation (e.g. 240 KB, 1.25 MB)
    attachmentFileSize() {
      if (this.isLoadingFileSize) return 'Calculating size...';
      if (this.fileBytes === null || this.fileBytes === undefined) return '';
      if (this.fileBytes === 0) return '0 B';

      const units = ['B', 'KB', 'MB', 'GB'];
      const index = Math.floor(Math.log(this.fileBytes) / Math.log(1024));
      const value = (this.fileBytes / Math.pow(1024, index)).toFixed(2);
      return `${parseFloat(value)} ${units[index]}`;
    }
  },
  mounted() {
  },
  methods: {
    async fetchFileSize(url) {
      this.isLoadingFileSize = true;
      this.fileBytes = null;

      try {
        // Attempt 1: Lightweight HEAD request
        let response = await fetch(url, { method: 'HEAD' });

        // Attempt 2: If S3 signature rejects HEAD (HTTP 403), use a 1-byte GET request
        if (response.status === 403) {
          response = await fetch(url, {
            method: 'GET',
            headers: { Range: 'bytes=0-0' }
          });

          const contentRange = response.headers.get('content-range');
          if (contentRange) {
            // Header format: "bytes 0-0/123456"
            const totalBytes = contentRange.split('/')[1];
            if (totalBytes && !isNaN(totalBytes)) {
              this.fileBytes = parseInt(totalBytes, 10);
              return;
            }
          }
        }

        const length = response.headers.get('content-length');
        if (length) {
          this.fileBytes = parseInt(length, 10);
        }
      } catch (err) {
        console.warn('Could not retrieve file size via headers:', err);
      } finally {
        this.isLoadingFileSize = false;
      }
    },

    closePopup() {
      document.body.classList.remove('body_right_scroll_remove');
      bus.$emit('replyMessageFullPreviewBus', false);
    }
  }
};
</script>

<style lang="scss" scoped>
// Design Tokens
$bg-dark: #0f172a;
$card-dark: #000000;
$border-dark: #334155;
$text-primary-dark: #f8fafc;
$text-secondary-dark: #94a3b8;

$primary: #3b82f6;
$primary-hover: #2563eb;
$success: #10b981;
$radius: 12px;

.message-module-wrapper {
  color: $text-primary-dark;
  font-family: -apple-system,
  BlinkMacSystemFont,
  "Segoe UI",
  "Helvetica Neue",
  Arial,
  sans-serif,
  "Apple Color Emoji",
  "Segoe UI Emoji",
  "Segoe UI Symbol",
  "Noto Color Emoji";
}

// MODAL OVERLAY & CONTAINER
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: rgba(0, 0, 0, 0.92);
}

.modal-panel {
  width: 100%;
  max-width: 620px;
  background: $card-dark;
  border-radius: 6px;
  box-shadow: 0 0 13px rgba(255, 213, 0, .6);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 88vh;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &.is-minimized {
    max-height: 60px;
    align-self: flex-end;
    margin-bottom: 24px;
  }
}

// HEADER
.modal-header {
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: end;
  border-bottom: 1px solid rgba(125, 125, 125, 0.15);

  .header-left {
    display: flex;
    gap: 8px;
  }

  .badge {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    padding: 3px 8px;
    border-radius: 4px;

    &-tag {
      background: rgba($primary, 0.15);
      color: $primary;
    }

    &-priority {
      background: rgba(#ef4444, 0.15);
      color: #ef4444;
    }
  }

  .header-actions {
    display: flex;
    gap: 6px;

    .icon-action-btn {
      background: rgba(125, 125, 125, 0.1);
      color: #ffffff;
      border: none;
      width: 28px;
      height: 28px;
      border-radius: 6px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 600;

      &:hover {
        background: rgba(255, 58, 48, .12);
        color: #ff3a30;
      }
    }
  }
}

// SCROLLABLE AREA
.modal-scroll-area {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  
  &::-webkit-scrollbar {
      width: 5px;
      background: #000
  }
  &::-webkit-scrollbar-thumb {
      background-color: #232323;
      border-radius: 10px
  }
}

// SENDER META
.message-meta-section {

  .sender-profile {
    display: flex;
    align-items: center;
    gap: 12px;

    .avatar-large {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      object-fit: cover;
    }

    .profile-details {
      .name-line {
        display: flex;
        align-items: baseline;
        gap: 6px;

        h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
        }

        .sender-email {
          font-size: 12px;
          opacity: 0.6;
        }
      }

      .recipient-line {
        font-size: 12px;
        opacity: 0.75;
        margin-top: 2px;

        .recipient-badge {
          font-weight: 500;
        }

        .bullet {
          margin: 0 4px;
        }
      }
    }
  }
}

// BODY
.message-body-section {
  font-size: 14.5px;
  line-height: 1.3;
  padding: 12px 0 16px;
  color:#bcbcba;
  text-align: justify;

  .attachments-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 16px;

    .attachment-chip {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 16px 8px;
      border: 1px solid rgba(125, 125, 125, 0.2);
      border-radius: 6px;
      font-size: 14px;
      color: #9b9b98;
      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
        color: #ffffff;
      }

      .file-name {
        font-weight: 500;
      }

      .file-size {
        opacity: 0.6;
        margin-left: 4px;
      }
    }
  }
}


// SPINNER
.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// TRANSITIONS
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.modal-fade-enter {
  opacity: 0;
  transform: scale(0.96);
}

.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.97);
}
</style>